import axios from 'axios';

import { Phone, PhoneListResult } from '@/interfaces/phone';
import { AdminReviewsPage, RatingType, Review } from '@/interfaces/review';

import { getValue } from './localStorage';
import { normalizeBranch, NormalizedBranchInfo } from './normalizeBranch';

export {
  getAllProfiles,
  getProfileById,
  getProfileByNickname,
  getProfileByPhone,
  searchByNickname,
} from './profileQueries';

export type { ProfilesListResult } from '@/interfaces/profile';
export type { Phone, PhoneListResult } from '@/interfaces/phone';

axios.interceptors.request.use((req) => {
  return req;
});

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    return Promise.reject(error);
  }
);

// CURRENCY

export const getCurrencyList = async (page: number, maxResults: number) => {
  const res = await axios.get(
    process.env.REACT_APP_CURRENCIES_API_URL + `/list`,
    {
      params: {
        type: 'money',
        cpp: maxResults,
        page,
        'va-list': true,
      },
    }
  );

  return res.data;
};

export const getCurrenciesSpread = async (currencies: string) => {
  const res = await axios.get(
    process.env.REACT_APP_API_URL + `/api/v2/currencies/spreads?${currencies}`
  );

  return res.data;
};

export const getLocationSearch = async (q: string, city?: number) => {
  const res = await axios.get(
    process.env.REACT_APP_SITE_URL + `/api/location/v1/uk/search`,
    {
      params: {
        limit: 5,
        q,
        ...(city ? { city } : {}),
      },
    }
  );

  return res.data;
};

export const saveCurrencySpread = async (currency: string, value: number) => {
  const res = await axios.put(
    process.env.REACT_APP_API_URL +
      `/api/v2/admin/currencies/${currency}/spread`,
    {
      value,
    },
    {
      headers: {
        Authorization: `Bearer ${getValue('access_token')}`,
      },
    }
  );

  return res.data;
};

export const deleteCurrencySpread = async (currency: string) => {
  const res = await axios.delete(
    process.env.REACT_APP_API_URL +
      `/api/v2/admin/currencies/${currency}/spread`,
    {
      headers: {
        Authorization: `Bearer ${getValue('access_token')}`,
      },
    }
  );

  return res.data;
};

// PROFILE

export const changeProfileType = async (
  id: string,
  type: string,
  agree: boolean
) => {
  const res = await axios.post(
    process.env.REACT_APP_API_URL + `/api/v2/admin/profiles/${id}/change-type`,
    {
      profile_type: type,
      agree: agree,
    },
    {
      headers: {
        Authorization: `Bearer ${getValue('access_token')}`,
      },
    }
  );

  return res.data;
};

export const createBranch = async (
  isActive: boolean,
  address: string,
  city: number,
  metro: number | null | undefined,
  location: any,
  services: any,
  workTime: any,
  office?: { name?: string },
  addressClarification?: string
) => {
  const officeName = office?.name?.trim();
  const clarification = addressClarification?.trim();
  const res = await axios.post(
    process.env.REACT_APP_API_URL + `/api/v2/admin/branches`,
    {
      branch_type: 'exchanger',
      location: location,
      address: address,
      city: city,
      ...(Number(metro) > 0 ? { metro: Number(metro) } : {}),
      work_time: workTime,
      is_active: isActive,
      auto_update: false,
      services: services,
      ...(officeName ? { office: { name: officeName } } : {}),
      ...(clarification ? { address_clarification: clarification } : {}),
    },
    {
      headers: {
        Authorization: `Bearer ${getValue('access_token')}`,
      },
    }
  );

  return res.data;
};

// SUBSCRIPTION

export const addSubscription = async (
  id: string,
  serviceId: string,
  activeAt: string,
  count: number
) => {
  const res = await axios.post(
    process.env.REACT_APP_API_URL + `/api/v2/admin/profiles/${id}/subscription`,
    {
      service_product_id: serviceId,
      active_at: activeAt,
      count_items: count,
    },
    {
      headers: {
        Authorization: `Bearer ${getValue('access_token')}`,
      },
    }
  );

  return res.data;
};

export const addCustomPayment = async (
  id: string,
  serviceProductId: string,
  paymentExpiresDays: number,
  paymentCount: number,
  countItems: number,
  amount?: number,
  periodDays?: number,
  activeAt?: string
) => {
  const res = await axios({
    method: 'POST',
    url: `${process.env.REACT_APP_API_URL}/api/v2/admin/profiles/${id}/subscription`,
    headers: {
      Authorization: `Bearer ${getValue('access_token')}`,
    },
    data: {
      pay_by_link: true,
      service_product_id: serviceProductId,
      payment_expires_days: paymentExpiresDays,
      payment_count: paymentCount,
      amount: amount || null,
      period_days: periodDays || null,
      active_at: activeAt || null,
      count_items: countItems,
    },
  });

  return res.data;
};

export const sendPaymentNotification = async (
  id: string,
  type: string,
  data: string
) => {
  const res = await axios.post(
    process.env.REACT_APP_API_URL + `/api/v2/admin/profiles/${id}/pay-links`,
    {
      [type]: data,
    },
    {
      headers: {
        Authorization: `Bearer ${getValue('access_token')}`,
      },
    }
  );

  return res.data;
};

export const deleteSubscription = async (id: string) => {
  const res = await axios.delete(
    process.env.REACT_APP_API_URL + `/api/v2/admin/profiles/${id}/subscription`,
    {
      headers: {
        Authorization: `Bearer ${getValue('access_token')}`,
      },
    }
  );

  return res.data;
};

export const deleteProfileSubscription = async (id: string) => {
  const res = await axios.delete(
    process.env.REACT_APP_API_URL + `/api/v2/admin/subscriptions/${id}`,
    {
      headers: {
        Authorization: `Bearer ${getValue('access_token')}`,
      },
    }
  );

  return res.data;
};

// PHONE

export const addPhone = async (phone: string, profileId: string) => {
  const path = process.env.REACT_APP_API_URL + '/api/v2/admin/phones/' + phone;

  const res = await axios.post(
    path,
    {
      profile_id: profileId,
    },
    {
      headers: {
        Authorization: `Bearer ${getValue('access_token')}`,
      },
    }
  );

  return res.data;
};

export const deletePhone = async (phone: string) => {
  const path = process.env.REACT_APP_API_URL + '/api/v2/admin/phones/' + phone;

  const res = await axios.delete(path, {
    headers: {
      Authorization: `Bearer ${getValue('access_token')}`,
    },
  });

  return res.data;
};

export const patchPhone = async (phone: string, newPhone: string) => {
  const path = process.env.REACT_APP_API_URL + '/api/v2/admin/phones/' + phone;

  const res = await axios.patch(
    path,
    {
      new_phone: newPhone,
    },
    {
      headers: {
        Authorization: `Bearer ${getValue('access_token')}`,
      },
    }
  );

  return res.data;
};

// BRANCH

export const getBranch = async (
  branchId: string
): Promise<NormalizedBranchInfo> => {
  const res = await axios.get(
    `${process.env.REACT_APP_API_URL}/api/v2/admin/branches/${branchId}`,
    {
      headers: {
        Authorization: `Bearer ${getValue('access_token')}`,
      },
    }
  );

  return normalizeBranch(res.data);
};

export const getAllBranches = async (
  page: number = 1,
  isFree: boolean = false
) => {
  const path = process.env.REACT_APP_API_URL + `/api/v2/admin/branches`;

  const res = await axios({
    method: 'GET',
    headers: {
      Authorization: `Bearer ${getValue('access_token')}`,
    },
    url: path,
    params: {
      page,
      size: 25,
      ...(isFree ? { free: true } : {}),
    },
  });

  return res.data;
};

interface BranchImageV2 {
  _id?: string;
  branch_image_id?: string;
  image_id?: string;
  image: string;
  preview: string;
}

export interface BranchPhoto {
  _id: string;
  image_id: string;
  branch_id: string;
  image: string;
  preview?: string;
  _created: string;
  _update: string;
}

export interface BranchPhotosListResult {
  items: BranchPhoto[];
  total: number;
  page: number;
  size: number;
}

const branchImagesHeaders = () => ({
  Authorization: `Bearer ${getValue('access_token')}`,
});

const extractObjectIdFromUrl = (url?: string): string | undefined => {
  if (!url) return undefined;

  const match = url.match(/\/([a-f0-9]{24})(?:\.[a-z0-9]+)?$/i);
  return match?.[1];
};

const mapBranchImagesV2 = (
  branchId: string,
  rawItems: BranchImageV2[]
): BranchPhotosListResult => {
  const items = rawItems.map((item) => {
    const branchImageId = item._id ?? item.branch_image_id ?? '';

    return {
      _id: branchImageId,
      image_id: item.image_id ?? '',
      branch_id: branchId,
      image: item.image,
      preview: item.preview,
      _created: '',
      _update: '',
    };
  });

  return {
    items,
    total: items.length,
    page: 1,
    size: items.length,
  };
};

const branchImagesApiUrl = (path: string) =>
  `${process.env.REACT_APP_API_URL}/api/v2/admin/${path}`;

export const getBranchPhoto = async (
  branchId: string
): Promise<BranchPhotosListResult> => {
  const res = await axios.get<BranchImageV2[] | { items?: BranchImageV2[] }>(
    branchImagesApiUrl(`images/branches/${branchId}`),
    {
      headers: branchImagesHeaders(),
    }
  );

  const items = Array.isArray(res.data) ? res.data : (res.data?.items ?? []);

  return mapBranchImagesV2(branchId, items);
};

export const postBranchPhoto = async (branchId: string, file: File) => {
  const formData = new FormData();
  formData.append('file', file);

  const res = await axios.post(
    branchImagesApiUrl(`images/branches/${branchId}`),
    formData,
    {
      headers: branchImagesHeaders(),
    }
  );

  return res.data;
};

export const getUploadedImageId = (data: any): string | undefined => {
  if (!data) return undefined;
  if (typeof data === 'string' && /^[a-f0-9]{24}$/i.test(data)) return data;

  const candidate =
    data.image_id ||
    data._id ||
    data.branch_image_id ||
    data.data?.image_id ||
    data.data?._id;

  if (typeof candidate === 'string' && candidate) return candidate;

  return extractObjectIdFromUrl(data.image || data.data?.image);
};

export const patchBranchOffice = async (
  id: string,
  office: { name?: string; logo?: string | null }
) => {
  const res = await axios.patch(
    process.env.REACT_APP_API_URL + `/api/v2/admin/branches/${id}`,
    { office },
    {
      headers: {
        Authorization: `Bearer ${getValue('access_token')}`,
      },
    }
  );

  return res.data;
};

export const attachOfficeLogo = async (branchId: string, file: File) => {
  const uploaded = await postBranchPhoto(branchId, file);
  const imageId = getUploadedImageId(uploaded);

  if (!imageId) {
    throw new Error('Не вдалося визначити id завантаженого логотипу');
  }

  return patchBranchOffice(branchId, { logo: imageId });
};

export const deleteBranchPhoto = async (id: string) => {
  const res = await axios.delete(branchImagesApiUrl(`branches/images/${id}`), {
    headers: branchImagesHeaders(),
  });

  return res.data;
};

export const replaceBranchPhoto = async (
  branchImageId: string,
  image: string,
  _branchId?: string
) => {
  const res = await axios.patch(
    branchImagesApiUrl(`branches/images/${branchImageId}`),
    { image },
    {
      headers: branchImagesHeaders(),
    }
  );

  return res.data;
};

export const getBranchByProfileId = async (
  profileId: string,
  page: number = 1,
  size: number = 25
) => {
  const res = await axios({
    method: 'GET',
    url: `${process.env.REACT_APP_API_URL}/api/v2/admin/branches`,
    params: {
      page,
      size,
      profile_id: profileId,
    },
    headers: {
      Authorization: `Bearer ${getValue('access_token')}`,
    },
  });

  return res.data;
};

export const patchBranch = async (
  id: string,
  payload: {
    phone_id?: string | null;
    branch_type?: string | null;
    location?: { type: string; coordinates: number[] };
    city?: number;
    district?: string | null;
    metro?: number | null;
    address?: string;
    address_clarification?: string;
    work_time?: Record<string, unknown>;
    services?: {
      cctv: boolean;
      damaged_bills: boolean;
      recount_room: boolean;
      parking: boolean;
      blackout_enabled: boolean;
    };
    description?: string | null;
    is_active?: boolean;
    office?: { name?: string | null; logo?: string | null };
  }
) => {
  const {
    phone_id,
    branch_type,
    metro,
    district,
    description,
    office,
    address_clarification,
    ...rest
  } = payload;
  const officeBody = {
    ...(office?.name?.trim() ? { name: office.name.trim() } : {}),
    ...(office?.logo ? { logo: office.logo } : {}),
  };
  const clarification = address_clarification?.trim();
  const body = {
    ...rest,
    ...(phone_id ? { phone_id } : {}),
    ...(branch_type ? { branch_type } : {}),
    ...(Number(metro) > 0 ? { metro: Number(metro) } : {}),
    ...(district?.trim() ? { district: district.trim() } : {}),
    ...(description?.trim() ? { description: description.trim() } : {}),
    ...(Object.keys(officeBody).length > 0 ? { office: officeBody } : {}),
    ...(clarification ? { address_clarification: clarification } : {}),
  };

  const res = await axios.patch(
    process.env.REACT_APP_API_URL + `/api/v2/admin/branches/${id}`,
    body,
    {
      headers: {
        Authorization: `Bearer ${getValue('access_token')}`,
      },
    }
  );

  return res.data;
};

export const getAdminBranchRateById = async (branchId: string) => {
  const headers = {
    Authorization: `Bearer ${getValue('access_token')}`,
  };
  const url = `${process.env.REACT_APP_API_URL}/api/v2/admin/branches/rates`;

  const fetchPage = async (params: Record<string, string | number>) => {
    const res = await axios.get(url, { headers, params });
    return res.data;
  };

  const filterByBranch = (items: { branch_id: string }[] = []) =>
    items.filter((rate) => rate.branch_id === branchId);

  try {
    const data = await fetchPage({
      branch_id: branchId,
      page: 1,
      size: 50,
    });

    return {
      ...data,
      items: filterByBranch(data.items),
    };
  } catch (error: any) {
    if (error?.response?.status !== 422) {
      throw error;
    }
  }

  const collected: { branch_id: string }[] = [];
  let page = 1;
  const size = 50;

  while (page <= 70) {
    const data = await fetchPage({ page, size });
    const items = filterByBranch(data.items);
    collected.push(...items);

    const total = data.total ?? 0;
    if (page * size >= total) {
      break;
    }

    page += 1;
  }

  return {
    items: collected,
    total: collected.length,
    page: 1,
    size: collected.length,
  };
};

export const changeBranchRate = async (
  id: string,
  currency: string,
  buyValue: number,
  sellValue: number,
  buyMaxCount: number,
  buyMinCount: number,
  sellMaxCount: number,
  sellMinCount: number,
  trade?: any
) => {
  const res = await axios.post(
    process.env.REACT_APP_API_URL + `/api/v2/admin/branches/${id}/rates`,
    {
      currency: currency.toLowerCase(),
      rate: {
        buy: {
          max_count: buyMaxCount,
          min_count: buyMinCount,
          value: buyValue,
        },
        sell: {
          max_count: sellMaxCount,
          min_count: sellMinCount,
          value: sellValue,
        },
      },
      trade: trade,
    },
    {
      headers: {
        Authorization: `Bearer ${getValue('access_token')}`,
      },
    }
  );

  return res.data;
};

export const changeCustomBranchRate = async (
  courseType: string,
  sellExact: boolean,
  buyExact: boolean,
  id: string,
  currency: string,
  buyValue: number,
  sellValue: number,
  buyMaxCount: number,
  buyMinCount: number,
  sellMaxCount: number,
  sellMinCount: number,
  trade?: any
) => {
  let buy = {};
  let sell = {};
  let data = {
    currency: currency.toLowerCase(),
    rate: {},
    trade: trade,
  };

  if (!buyExact) {
    buy = {
      value: buyValue,
      min_count: buyMinCount,
      max_count: buyMaxCount,
    };
  }
  if (!sellExact) {
    sell = {
      value: sellValue,
      min_count: sellMinCount,
      max_count: sellMaxCount,
    };
  }
  if (buyExact) {
    buy = {
      value: buyValue,
      min_count: buyMinCount,
    };
  }
  if (sellExact) {
    sell = {
      min_count: sellMinCount,
      value: sellValue,
    };
  }
  if (courseType === 'sell') {
    data.rate = { sell: sell };
  }
  if (courseType === 'buy') {
    data.rate = { buy: buy };
  }
  if (courseType === 'buy/sell') {
    data.rate = { buy: buy, sell: sell };
  }

  const res = await axios.post(
    process.env.REACT_APP_API_URL + `/api/v2/admin/branches/${id}/rates`,
    data,
    {
      headers: {
        Authorization: `Bearer ${getValue('access_token')}`,
      },
    }
  );

  return res.data;
};

export const deleteBranchRateById = async (id: string) => {
  const res = await axios.delete(
    process.env.REACT_APP_API_URL + `/api/v2/admin/branches/rates/${id}`,
    {
      headers: {
        Authorization: `Bearer ${getValue('access_token')}`,
      },
    }
  );

  return res.data;
};

// REVIEW

export const reviewerInfo = async (id: string) => {
  const path = process.env.REACT_APP_API_URL + '/api/v2/profiles/' + id;

  const res = await axios({
    method: 'GET',
    url: path,
    headers: {
      Authorization: `Bearer ${getValue('access_token')}`,
    },
  });
  return res.data;
};

export const applicationsList = async (
  page = 1,
  status = 'open',
  maxResults = 8
) => {
  const res = await axios({
    method: 'GET',
    url: process.env.REACT_APP_API_URL + `/api/v2/admin/branches/free/owners`,
    headers: {
      Authorization: `Bearer ${getValue('access_token')}`,
    },
    params: {
      page,
      size: maxResults,
      status,
    },
  });

  return res.data;
};

export const getFreeBranchCorrections = async (
  page = 1,
  status = 'open',
  maxResults = 8
) => {
  const res = await axios({
    method: 'GET',
    url:
      process.env.REACT_APP_API_URL + `/api/v2/admin/branches/free/corrections`,
    headers: {
      Authorization: `Bearer ${getValue('access_token')}`,
    },
    params: {
      page,
      size: maxResults,
      status,
    },
  });

  return res.data;
};
export const approveFreeBranchCorrection = async (id: string) => {
  const res = await axios.post(
    process.env.REACT_APP_API_URL +
      `/api/v2/admin/branches/free/corrections/apply`,
    {
      accepted: id,
    },
    {
      headers: {
        Authorization: `Bearer ${getValue('access_token')}`,
      },
    }
  );

  return res;
};

export const declineFreeBranchCorrection = async (id: string) => {
  const res = await axios.post(
    process.env.REACT_APP_API_URL +
      `/api/v2/admin/branches/free/corrections/apply`,
    {
      rejected: [id],
    },
    {
      headers: {
        Authorization: `Bearer ${getValue('access_token')}`,
      },
    }
  );

  return res;
};

export const applicationFreeOwners = async (id: string) => {
  const path =
    process.env.REACT_APP_API_URL + `/api/v2/admin/branches/free/owners/${id}`;

  const res = await axios({
    method: 'GET',
    url: path,
    headers: {
      Authorization: `Bearer ${getValue('access_token')}`,
    },
  });

  return res.data;
};

export const changeStatusType = async (id: string, status: string) => {
  const res = await axios.patch(
    process.env.REACT_APP_API_URL +
      `/api/v2/admin/branches/free/owners/${id}/status/${status}`,
    {},
    {
      headers: {
        Authorization: `Bearer ${getValue('access_token')}`,
      },
    }
  );

  return res.data;
};

export const changeApplicationFreeOwners = async (
  id: string,
  ownerData: object
) => {
  const res = await axios.patch(
    process.env.REACT_APP_API_URL + `/api/v2/admin/branches/free/owners/${id}`,
    { ...ownerData },
    {
      headers: {
        Authorization: `Bearer ${getValue('access_token')}`,
      },
    }
  );

  return res;
};

const ratingConnotationParam = (rating: `${RatingType}`) => {
  switch (rating) {
    case RatingType.Negative:
      return 'negative';
    case RatingType.Positive:
      return 'positive';
    default:
      return undefined;
  }
};

type AdminReviewType = 'branch' | 'profile' | 'booking';

type AdminReviewsQuery = {
  page: number;
  size: number;
  sort: string;
  status: string;
  rating_connotation?: string;
};

const buildAdminReviewsQuery = ({
  page,
  size,
  sort,
  status,
  rating_connotation,
}: AdminReviewsQuery) => {
  const params = new URLSearchParams();

  params.set('page', String(page));
  params.set('size', String(size));
  params.set('sort', sort);
  params.append('status', status);

  if (rating_connotation) {
    params.set('rating_connotation', rating_connotation);
  }

  return params.toString();
};

/** v2 admin reviews повертає масив Review[] без обгортки пагінації */
const estimateReviewTotal = (count: number, page: number, pageSize: number) =>
  count < pageSize ? (page - 1) * pageSize + count : page * pageSize + 1;

const fetchAdminReviews = async (
  url: string,
  page: number,
  status: string,
  ratingConnotation: string | undefined,
  reviewType: AdminReviewType,
  maxResults: number
): Promise<AdminReviewsPage> => {
  try {
    // URLSearchParams → page=1&size=16&sort=-_updated&status=active&...
    const query = buildAdminReviewsQuery({
      page,
      size: maxResults,
      sort: '-_updated',
      status,
      rating_connotation: ratingConnotation,
    });

    const { data } = await axios.get(`${url}?${query}`, {
      headers: {
        Authorization: `Bearer ${getValue('access_token')}`,
      },
    });

    const rawItems = (Array.isArray(data) ? data : []) as Review[];

    return {
      items: rawItems.map((review) => ({ ...review, type: reviewType })),
      total: estimateReviewTotal(rawItems.length, page, maxResults),
    };
  } catch (error) {
    console.error(`Failed to fetch ${reviewType} admin reviews:`, error);
    return { items: [], total: 0 };
  }
};

export const reviewList = async (
  page = 1,
  filter = {
    status: 'created',
    rating: 'all',
  },
  maxResults = 8
): Promise<AdminReviewsPage> => {
  const branchesReviesPath =
    process.env.REACT_APP_API_URL + '/api/v2/admin/branches/reviews/';
  const profilesReviewsPath =
    process.env.REACT_APP_API_URL + '/api/v2/admin/customers/reviews/';
  const profilesBookingReviewsPath =
    process.env.REACT_APP_API_URL + '/api/v2/admin/booking/clients/reviews/';

  const { status, rating } = filter;
  const reviewRatingConnotation = ratingConnotationParam(
    rating as `${RatingType}`
  );

  const [branch, profile, booking] = await Promise.all([
    fetchAdminReviews(
      branchesReviesPath,
      page,
      status,
      reviewRatingConnotation,
      'branch',
      maxResults
    ),
    fetchAdminReviews(
      profilesReviewsPath,
      page,
      status,
      reviewRatingConnotation,
      'profile',
      maxResults
    ),
    fetchAdminReviews(
      profilesBookingReviewsPath,
      page,
      status,
      reviewRatingConnotation,
      'booking',
      maxResults
    ),
  ]);

  const items = [...branch.items, ...profile.items, ...booking.items].sort(
    (a, b) => new Date(b._created).getTime() - new Date(a._created).getTime()
  );

  return {
    items,
    total: branch.total + profile.total + booking.total,
  };
};

//ADDRESSES

export const getAddressChanges = async (page: number = 1) => {
  const path = `${process.env.REACT_APP_API_URL}/api/v2/admin/branches/addresses`;

  const res = await axios({
    method: 'GET',
    url: path,
    headers: {
      Authorization: `Bearer ${getValue('access_token')}`,
    },
    params: {
      page,
      size: 15,
    },
  });

  return res.data;
};
export const approveAddressChange = async ({
  id,
  key,
}: {
  id: string;
  key: string;
}) => {
  const res = await axios.post(
    `${process.env.REACT_APP_API_URL}/api/v2/admin/branches/${id}/address/approve/${key}`,
    {},
    {
      headers: {
        Authorization: `Bearer ${getValue('access_token')}`,
      },
    }
  );

  return res.data;
};

export const rejectAddressChange = async ({
  id,
  key,
}: {
  id: string;
  key: string;
}) => {
  const res = await axios.post(
    `${process.env.REACT_APP_API_URL}/api/v2/admin/branches/${id}/address/reject/${key}`,
    {},
    {
      headers: {
        Authorization: `Bearer ${getValue('access_token')}`,
      },
    }
  );

  return res.data;
};

export const getNewOffices = async (page: number = 1) => {
  const path = `${process.env.REACT_APP_API_URL}/api/v2/admin/offices/new`;

  const res = await axios({
    method: 'GET',
    url: path,
    headers: {
      Authorization: `Bearer ${getValue('access_token')}`,
    },
    params: {
      page,
      size: 15,
    },
  });

  return res.data;
};
export const getOffices = async (page: number = 1) => {
  const path = `${process.env.REACT_APP_API_URL}/api/v2/admin/offices/`;

  const res = await axios({
    method: 'GET',
    url: path,
    headers: {
      Authorization: `Bearer ${getValue('access_token')}`,
    },
    params: {
      page,
      size: 15,
    },
  });

  return res.data;
};

export const rejectNewOffice = async ({
  id,
  key,
}: {
  id: string;
  key: string;
}) => {
  const res = await axios.post(
    `${process.env.REACT_APP_API_URL}/api/v2/admin/offices/${id}/new/reject/${key}`,
    {},
    {
      headers: {
        Authorization: `Bearer ${getValue('access_token')}`,
      },
    }
  );

  return res.data;
};

export const approveNewOffice = async ({
  id,
  key,
}: {
  id: string;
  key: string;
}) => {
  const res = await axios.post(
    `${process.env.REACT_APP_API_URL}/api/v2/admin/offices/${id}/new/approve/${key}`,
    {},
    {
      headers: {
        Authorization: `Bearer ${getValue('access_token')}`,
      },
    }
  );

  return res.data;
};

// OTHER

export const getServiceProduct = async (type: string) => {
  const res = await axios.get(
    process.env.REACT_APP_API_URL + `/api/v2/products/${type}`
  );

  return res.data;
};

export const getAllCities = async () => {
  const path = process.env.REACT_APP_SITE_URL + '/api/cities';

  const res = await axios({
    method: 'GET',
    url: path,
  });

  return res.data;
};

export const getAllMetro = async () => {
  const path =
    process.env.REACT_APP_SITE_URL + '/api/get/metro/v2/list/?locale=uk';

  const res = await axios({
    method: 'GET',
    url: path,
  });

  return res.data;
};

export const getAllDistricts = async () => {
  const path = process.env.REACT_APP_API_URL + '/api/v2/dicts/districts';

  const res = await axios({
    method: 'GET',
    url: path,
  });

  return res.data;
};

export const subscribeInfo = async (id: string) => {
  const path =
    process.env.REACT_APP_API_URL + `/api/v2/admin/profiles/${id}/subscription`;

  const res = await axios({
    method: 'GET',
    url: path,
    headers: {
      Authorization: `Bearer ${getValue('access_token')}`,
    },
  });

  const data = res.data;

  return {
    ...data,
    firstName: data.firstname ?? data.firstName,
    lastName: data.lastname ?? data.lastName,
    subscribe: data.subscribe ?? data.subscription?.status === 'active',
    active: data.active ?? data.suspended === false,
  };
};

export const applicationInfo = async (id: string) => {
  const path = `${process.env.REACT_APP_API_URL}/api/v2/applications`;

  const res = await axios({
    method: 'GET',
    url: path,
    headers: {
      Authorization: `Bearer ${getValue('access_token')}`,
    },
    params: {
      profile_id: id,
      size: 1,
      page: 1,
      embedded: true,
    },
  });

  return res.data;
};

export const userAuthorization = async () => {
  const path = process.env.REACT_APP_MINFIN_AUTH_URL;

  const res = await axios({
    method: 'GET',
    url: path,
    withCredentials: true,
  });
  return res.data;
};

interface ProfilePhonesV2Response {
  profile_id: string;
  phones: Array<{
    id: string;
    phone: string;
    verified: boolean;
  }>;
}

const mapProfilePhonesV2 = (data: ProfilePhonesV2Response): PhoneListResult => {
  const items: Phone[] = (data.phones ?? []).map((item) => ({
    _id: item.id,
    phone: item.phone,
    verified: item.verified,
    profile_id: data.profile_id,
    _created: '',
    _updated: '',
  }));

  return {
    profile_id: data.profile_id,
    items,
    total: items.length,
    page: 1,
    size: items.length,
  };
};

export const searchByPhone = async (id: string): Promise<PhoneListResult> => {
  const path = process.env.REACT_APP_API_URL + `/api/v2/phones/profile/${id}`;

  const res = await axios.get<ProfilePhonesV2Response>(path, {
    headers: {
      Authorization: `Bearer ${getValue('access_token')}`,
    },
  });

  return mapProfilePhonesV2(res.data);
};

export const adminGetDashboard = async () => {
  const path = process.env.REACT_APP_API_URL + `/api/v2/admin/dashboard`;

  const res = await axios({
    method: 'GET',
    url: path,
    headers: {
      Authorization: `Bearer ${getValue('access_token')}`,
    },
  });

  return res.data;
};

export const adminPostDashboard = async (url: string, name?: string) => {
  const path = process.env.REACT_APP_API_URL + `${url}`;

  const requestOptions: any = {
    method: 'POST',
    url: path,
    headers: {
      Authorization: `Bearer ${getValue('access_token')}`,
    },
  };

  // Додаємо поле 'data', якщо 'name' визначено
  if (name) {
    requestOptions.data = {
      kind: name,
    };
  }

  const res = await axios(requestOptions);
  return res.data;
};

export const subscriptionBonusAddDays = async (
  profileId: string,
  days: number,
  comment?: string
) => {
  const path =
    process.env.REACT_APP_API_URL +
    `/api/v2/admin/profiles/${profileId}/subscription/bonus/add-days`;

  const res = await axios({
    method: 'POST',
    url: path,
    headers: {
      Authorization: `Bearer ${getValue('access_token')}`,
    },
    data: {
      days,
      ...(comment ? { comment } : {}),
    },
  });

  return res.data;
};
export const subscriptionBonusChangePrice = async (
  profileId: string,
  price: number,
  comment?: string
) => {
  const path =
    process.env.REACT_APP_API_URL +
    `/api/v2/admin/profiles/${profileId}/subscription/bonus/change-price`;

  const res = await axios({
    method: 'POST',
    url: path,
    headers: {
      Authorization: `Bearer ${getValue('access_token')}`,
    },
    data: {
      price,
      ...(comment ? { comment } : {}),
    },
  });

  return res.data;
};
export const subscriptionBonusChangeCount = async (
  profileId: string,
  count: number,
  comment?: string
) => {
  const path =
    process.env.REACT_APP_API_URL +
    `/api/v2/admin/profiles/${profileId}/subscription/bonus/change-count`;

  const res = await axios({
    method: 'POST',
    url: path,
    headers: {
      Authorization: `Bearer ${getValue('access_token')}`,
    },
    data: {
      count,
      ...(comment ? { comment } : {}),
    },
  });

  return res.data;
};
export const getSubscriptionRealPrice = async (id: string) => {
  const path =
    process.env.REACT_APP_API_URL +
    `/api/v2/admin/profiles/${id}/subscription/real-price`;

  const res = await axios({
    method: 'GET',
    url: path,
    headers: {
      Authorization: `Bearer ${getValue('access_token')}`,
    },
  });

  return res.data;
};

export const getNotifications = async () => {
  const path = process.env.REACT_APP_API_URL + `/api/v2/admin/notifications`;

  const res = await axios({
    method: 'GET',
    url: path,
    headers: {
      Authorization: `Bearer ${getValue('access_token')}`,
    },
  });

  return res.data;
};

export const getPhotos = async () => {
  const path =
    process.env.REACT_APP_API_URL + `/api/v2/admin/images/branches/pending`;

  const res = await axios({
    method: 'GET',
    url: path,
    headers: {
      Authorization: `Bearer ${getValue('access_token')}`,
    },
  });

  return res.data.items;
};

export const approvePhoto = async ({ imageId }: { imageId: string }) => {
  const res = await axios.post(
    `${process.env.REACT_APP_API_URL}/api/v2/admin/images/${imageId}/approve`,
    {},
    {
      headers: {
        Authorization: `Bearer ${getValue('access_token')}`,
      },
    }
  );

  return res;
};

export const rejectPhoto = async ({ imageId }: { imageId: string }) => {
  const res = await axios.delete(
    `${process.env.REACT_APP_API_URL}/api/v2/admin/images/${imageId}/pending`,
    {
      headers: {
        Authorization: `Bearer ${getValue('access_token')}`,
      },
    }
  );

  return res;
};

export const getPrivateUserInfo = async () => {
  const path = process.env.REACT_APP_API_URL + `/api/v2/user/info`;

  const res = await axios({
    method: 'GET',
    url: path,
    headers: {
      Authorization: `Bearer ${getValue('access_token')}`,
    },
  });
  return res.data;
};

export const generatedQrPdf = async (ids: string[]): Promise<Blob> => {
  const path = `${process.env.REACT_APP_API_URL}/api/v2/admin/qrcodes/pdf`;

  const res = await axios.post(
    path,
    {
      exchanger_ids: ids,
    },
    {
      headers: {
        Authorization: `Bearer ${getValue('access_token')}`,
      },
      responseType: 'blob',
    }
  );

  return res.data;
};

// WALLETS

export const getWallets = async (
  page: number = 1,
  size: number = 50,
  nickname?: string
) => {
  const path = `${process.env.REACT_APP_API_URL}/api/v2/admin/wallets`;

  const params: any = {
    page,
    size,
  };

  if (nickname) {
    params.nickname = nickname;
  }

  const res = await axios({
    method: 'GET',
    url: path,
    headers: {
      Authorization: `Bearer ${getValue('access_token')}`,
    },
    params,
  });

  return res.data;
};
